// src/components/GlobalSearchDropdown.tsx
//
// Renders the global search results panel that appears below the sidebar
// search input. Hits /api/auth/search with 250ms debounce and groups results
// by type. Closes when the user clicks a result, presses Escape, or clicks
// outside the panel.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";

type SearchResults = {
  applications: Array<{ id: string; jobTitle: string; company: string; status: string; location: string }>;
  savedJobs: Array<{ id: string; title: string; company: string; location: string; externalJobId: string; jobUrl: string }>;
  recommendations: Array<{ id: string; jobId: string; jobTitle: string; company: string; location: string; matchScore: number; url?: string }>;
  resumes: Array<{ id: string; fileName: string; isPrimary: boolean }>;
  builtResumes: Array<{ id: string; title: string }>;
  coverLetters: Array<{ id: string; title: string }>;
  discussions: Array<{ id: string; title: string; category: { displayName: string } | null; replyCount: number; likeCount: number }>;
  staffingFirms: Array<{ id: string; slug: string; name: string; services: string[]; isVerified: boolean }>;
};

type Response = {
  query: string;
  total: number;
  results: SearchResults;
};

type Props = {
  /** Current query (controlled by parent input). */
  query: string;
  /** Called when user picks a result or hits Escape, so parent can clear/close. */
  onClose: () => void;
  /**
   * Optional anchor element. When provided, the dropdown floats to the RIGHT
   * of this element (outside the sidebar, over the main content area) so it
   * never covers the filtered sidebar nav. When omitted, it falls back to the
   * old inline-below positioning.
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
};

const GlobalSearchDropdown: React.FC<Props> = ({ query, onClose, anchorRef }) => {
  const navigate = useNavigate();
  const [data, setData] = useState<Response | null>(null);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Computed fixed-position coordinates when anchored to a sidebar element.
  const [floatStyle, setFloatStyle] = useState<React.CSSProperties | null>(null);

  // Compute float-to-the-right position based on the anchor's bounding rect.
  // Recomputes on resize + scroll so the dropdown stays attached to the sidebar.
  useLayoutEffect(() => {
    if (!anchorRef?.current) {
      setFloatStyle(null);
      return;
    }
    const compute = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const gap = 12;
      const width = Math.min(420, window.innerWidth - rect.right - gap - 16);
      setFloatStyle({
        position: "fixed",
        top: rect.top,
        left: rect.right + gap,
        width: Math.max(280, width),
        maxHeight: window.innerHeight - rect.top - 24,
      });
    };
    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [anchorRef]);

  // Debounced fetch
  useEffect(() => {
    if (query.trim().length < 2) {
      setData(null);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      axiosInstance
        .get<Response>(`/auth/search?q=${encodeURIComponent(query)}&limit=4`)
        .then((r) => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        // Allow clicks on the parent sidebar search input itself
        const target = e.target as HTMLElement;
        if (!target.closest(".hb-sidebar-search")) onClose();
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [onClose]);

  const sections = useMemo(() => {
    if (!data) return [];
    const r = data.results;
    return [
      {
        key: "applications",
        title: "Applications",
        icon: "📋",
        items: r.applications.map((a) => ({
          id: a.id,
          primary: a.jobTitle,
          secondary: `${a.company} · ${a.status}`,
          to: `/applications`,
        })),
      },
      {
        key: "recommendations",
        title: "Recommendations",
        icon: "⭐",
        items: r.recommendations.map((rec) => ({
          id: rec.id,
          primary: rec.jobTitle,
          secondary: `${rec.company} · ${Math.round(rec.matchScore)}% match`,
          to: `/recommendations`,
        })),
      },
      {
        key: "savedJobs",
        title: "Saved jobs",
        icon: "📌",
        items: r.savedJobs.map((s) => ({
          id: s.id,
          primary: s.title,
          secondary: `${s.company}${s.location ? ` · ${s.location}` : ""}`,
          to: `/saved-jobs`,
        })),
      },
      {
        key: "resumes",
        title: "Resumes",
        icon: "📄",
        items: [
          ...r.resumes.map((res) => ({
            id: res.id,
            primary: res.fileName,
            secondary: res.isPrimary ? "Primary" : "Uploaded",
            to: `/resumes`,
          })),
          ...r.builtResumes.map((res) => ({
            id: res.id,
            primary: res.title,
            secondary: "Built resume",
            to: `/resume-builder/${res.id}`,
          })),
        ],
      },
      {
        key: "coverLetters",
        title: "Cover letters",
        icon: "✉️",
        items: r.coverLetters.map((c) => ({
          id: c.id,
          primary: c.title,
          secondary: "Cover letter",
          to: `/cover-letter-builder`,
        })),
      },
      {
        key: "staffingFirms",
        title: "Staffing firms",
        icon: "🤝",
        items: r.staffingFirms.map((f) => ({
          id: f.id,
          primary: `${f.name}${f.isVerified ? " ✓" : ""}`,
          secondary: f.services.slice(0, 3).join(", "),
          to: `/staffing-firms`,
        })),
      },
      {
        key: "discussions",
        title: "Community discussions",
        icon: "💬",
        items: r.discussions.map((d) => ({
          id: d.id,
          primary: d.title,
          secondary: `${d.category?.displayName || "Discussion"} · ${d.replyCount} replies · ${d.likeCount} likes`,
          to: `/community`,
        })),
      },
    ].filter((s) => s.items.length > 0);
  }, [data]);

  if (query.trim().length < 2) return null;

  // When anchored, float to the right of the sidebar (fixed coords from useLayoutEffect).
  // When not anchored, fall back to the previous inline-below behavior.
  const positionStyle: React.CSSProperties = floatStyle
    ? floatStyle
    : { position: "absolute", top: "100%", left: 0, right: 0, marginTop: 6, maxHeight: "70vh" };

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label="Global search results"
      style={{
        ...positionStyle,
        background: "#fff",
        border: "1px solid #cbd5e1",
        borderRadius: 8,
        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
        zIndex: 9000,
        overflowY: "auto",
      }}
    >
      <div style={{
        padding: "8px 12px", fontSize: 11, color: "#64748b", fontWeight: 700,
        textTransform: "uppercase", letterSpacing: 0.5, borderBottom: "1px solid #f1f5f9",
        display: "flex", justifyContent: "space-between", alignItems: "center",
      }}>
        <span>{loading ? "Searching..." : data ? `${data.total} result${data.total === 1 ? "" : "s"}` : ""}</span>
        <span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>Esc to close</span>
      </div>

      {!loading && data && data.total === 0 && (
        <div style={{ padding: 24, textAlign: "center", color: "#64748b", fontSize: 13 }}>
          No matches across your applications, jobs, resumes, or community.
        </div>
      )}

      {sections.map((section) => (
        <div key={section.key}>
          <div style={{
            padding: "8px 12px 4px", fontSize: 11, color: "#475569", fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.5, background: "#f8fafc",
          }}>
            <span aria-hidden="true" style={{ marginRight: 6 }}>{section.icon}</span>
            {section.title}
          </div>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {section.items.map((it) => (
              <li key={`${section.key}-${it.id}`}>
                <button
                  onClick={() => { navigate(it.to); onClose(); }}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    background: "transparent",
                    border: "none",
                    padding: "8px 12px",
                    cursor: "pointer",
                    borderTop: "1px solid #f1f5f9",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#eff6ff")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {it.primary}
                  </div>
                  <div style={{ fontSize: 11, color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {it.secondary}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default GlobalSearchDropdown;
